import { useMemo, useState } from "react";
import type { MemberLocation } from "./use-member-locations";
import { withToggled } from "./toggle-set";

/**
 * The remove side of the manage-locations dialog. What the admin ticked is
 * always cut down to the member's active locations as last read, so a location
 * that vanished (or was removed by someone else) can never be sent again, and
 * `names` merges the names of ticked rows (they may sit on another page of the
 * picker) with the member's own locations.
 */
export function useRemoveSelection(
  locations: readonly MemberLocation[],
  pickedNames: ReadonlyMap<string, string>,
) {
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());

  const activeIds = useMemo(() => locations.filter((l) => l.active).map((l) => l.id), [locations]);
  const toRemove = useMemo(() => {
    const active = new Set(activeIds);
    return new Set([...picked].filter((id) => active.has(id)));
  }, [picked, activeIds]);
  const names = useMemo(() => {
    const known = new Map(pickedNames);
    for (const location of locations) known.set(location.id, location.name);
    return known;
  }, [pickedNames, locations]);

  return {
    activeIds,
    toRemove,
    names,
    toggleRemove: (id: string) => setPicked((prev) => withToggled(prev, id)),
    clearRemove: () => setPicked(new Set()),
  };
}
